import { Component } from '@theme/component';
import { WishlistUpdateEvent } from '@theme/events';

/**
 * Client-side wishlist store backed by localStorage. Mirrors the persistence
 * shape of RecentlyViewed (recently-viewed-products.js): a de-duplicated
 * array of product ids under a single storage key, no server round trip.
 */
export class Wishlist {
  static #STORAGE_KEY = 'wishlistProducts';

  /**
   * @returns {string[]} The list of wishlisted product ids.
   */
  static getProducts() {
    try {
      const stored = localStorage.getItem(this.#STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  /**
   * @param {string} productId
   * @returns {boolean}
   */
  static has(productId) {
    return this.getProducts().includes(String(productId));
  }

  /**
   * Toggles a product in the wishlist.
   *
   * @param {string} productId
   * @returns {boolean} Whether the product is in the wishlist after toggling.
   */
  static toggle(productId) {
    const id = String(productId);
    const products = this.getProducts();
    const index = products.indexOf(id);
    let inWishlist;

    if (index === -1) {
      products.unshift(id);
      inWishlist = true;
    } else {
      products.splice(index, 1);
      inWishlist = false;
    }

    try {
      localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(products));
    } catch {
      // Storage unavailable (private browsing, quota exceeded): state stays in-memory for this toggle only.
    }

    document.dispatchEvent(
      new WishlistUpdateEvent({ productId: id, inWishlist, count: products.length })
    );

    return inWishlist;
  }
}

/**
 * A heart-icon toggle button that adds/removes the card's product from the
 * localStorage-backed wishlist and reflects the active state visually.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} toggle
 *
 * @extends Component<Refs>
 */
class WishlistButton extends Component {
  requiredRefs = ['toggle'];

  connectedCallback() {
    super.connectedCallback();
    this.#render(Wishlist.has(this.#productId));
  }

  get #productId() {
    return this.getAttribute('data-product-id') ?? '';
  }

  toggle() {
    const inWishlist = Wishlist.toggle(this.#productId);
    this.#render(inWishlist);
  }

  /**
   * @param {boolean} inWishlist
   */
  #render(inWishlist) {
    const { toggle } = this.refs;

    this.classList.toggle('is-active', inWishlist);
    toggle.setAttribute('aria-pressed', String(inWishlist));
    toggle.setAttribute(
      'aria-label',
      inWishlist ? this.dataset.removeLabel ?? '' : this.dataset.addLabel ?? ''
    );
  }
}

if (!customElements.get('wishlist-button')) {
  customElements.define('wishlist-button', WishlistButton);
}
